import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Button from './Button';

interface WebhookTarget {
  target_id: string;
  url: string;
  format: 'generic' | 'teams';
  event_types: string[];
  enabled: boolean;
}

interface NotificationSettingsData {
  zone_name: string;
  teams_webhook_url: string | null;
  webhook_enabled: boolean;
  badge_enabled: boolean;
  webhook_targets: WebhookTarget[];
  smtp_enabled: boolean;
  smtp_host: string | null;
  smtp_port: number;
  smtp_username: string | null;
  smtp_password_encrypted: string | null;
  smtp_use_tls: boolean;
  smtp_from_address: string | null;
  email_recipients: string[];
  email_event_types: string[];
}

interface FormState {
  teams_webhook_url: string;
  webhook_enabled: boolean;
  badge_enabled: boolean;
  webhook_targets: WebhookTarget[];
  smtp_enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_password_is_set: boolean;
  smtp_use_tls: boolean;
  smtp_from_address: string;
  email_recipients: string;
  email_event_types: string;
}

const EMPTY_FORM: FormState = {
  teams_webhook_url: '',
  webhook_enabled: false,
  badge_enabled: true,
  webhook_targets: [],
  smtp_enabled: false,
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_password_is_set: false,
  smtp_use_tls: true,
  smtp_from_address: '',
  email_recipients: '',
  email_event_types: '',
};

function toFormState(data: NotificationSettingsData): FormState {
  return {
    teams_webhook_url: data.teams_webhook_url || '',
    webhook_enabled: data.webhook_enabled,
    badge_enabled: data.badge_enabled,
    webhook_targets: data.webhook_targets,
    smtp_enabled: data.smtp_enabled,
    smtp_host: data.smtp_host || '',
    smtp_port: data.smtp_port,
    smtp_username: data.smtp_username || '',
    smtp_password: '',
    smtp_password_is_set: !!data.smtp_password_encrypted,
    smtp_use_tls: data.smtp_use_tls,
    smtp_from_address: data.smtp_from_address || '',
    email_recipients: data.email_recipients.join(', '),
    email_event_types: data.email_event_types.join(', '),
  };
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

interface ZoneNotificationSettingsProps {
  zoneName: string;
  onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

/**
 * Per-zone notification settings form -- the zone-scoped half of AGENTGOV-136.
 * A "zone" here is the same team/department boundary already used elsewhere
 * in the registry (a Keycloak group flagged as a zone); "_global" is the
 * reserved fallback used when a resource isn't in any zone.
 */
const ZoneNotificationSettings: React.FC<ZoneNotificationSettingsProps> = ({
  zoneName,
  onShowToast,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [original, setOriginal] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get<NotificationSettingsData>(`/api/notifications/settings/${encodeURIComponent(zoneName)}`)
      .then((response) => {
        if (cancelled) return;
        const next = toFormState(response.data);
        setOriginal(next);
        setForm(next);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Failed to load notification settings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [zoneName]);

  const updateTarget = (index: number, patch: Partial<WebhookTarget>) => {
    setForm((prev) => ({
      ...prev,
      webhook_targets: prev.webhook_targets.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  };

  const addTarget = () => {
    setForm((prev) => ({
      ...prev,
      webhook_targets: [
        ...prev.webhook_targets,
        {
          target_id: `webhook-${prev.webhook_targets.length + 1}-${Date.now()}`,
          url: '',
          format: 'generic',
          event_types: [],
          enabled: true,
        },
      ],
    }));
  };

  const removeTarget = (index: number) => {
    setForm((prev) => ({
      ...prev,
      webhook_targets: prev.webhook_targets.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        teams_webhook_url: form.teams_webhook_url || null,
        webhook_enabled: form.webhook_enabled,
        badge_enabled: form.badge_enabled,
        webhook_targets: form.webhook_targets,
        smtp_enabled: form.smtp_enabled,
        smtp_host: form.smtp_host || null,
        smtp_port: form.smtp_port,
        smtp_username: form.smtp_username || null,
        // Omit smtp_password entirely (undefined) to keep the stored
        // password unchanged; send "" only if the admin explicitly cleared it.
        ...(form.smtp_password
          ? { smtp_password: form.smtp_password }
          : form.smtp_password_is_set
            ? {}
            : { smtp_password: '' }),
        smtp_use_tls: form.smtp_use_tls,
        smtp_from_address: form.smtp_from_address || null,
        email_recipients: splitCsv(form.email_recipients),
        email_event_types: splitCsv(form.email_event_types),
      };
      const response = await axios.put<NotificationSettingsData>(
        `/api/notifications/settings/${encodeURIComponent(zoneName)}`,
        body
      );
      const next = toFormState(response.data);
      setOriginal(next);
      setForm(next);
      onShowToast?.(`Notification settings saved for "${zoneName}"`, 'success');
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to save notification settings';
      setError(msg);
      onShowToast?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = original !== null && JSON.stringify(original) !== JSON.stringify(form);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200 flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* In-app badge */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.badge_enabled}
            onChange={(e) => setForm({ ...form, badge_enabled: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <div>
            <span className="block font-medium text-gray-900 dark:text-white">In-app badge</span>
            <span className="block text-sm text-gray-600 dark:text-gray-400 mt-1">
              Show a count badge on the Settings gear icon for this zone's pending approvals.
            </span>
          </div>
        </label>
      </div>

      {/* Webhook targets */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.webhook_enabled}
            onChange={(e) => setForm({ ...form, webhook_enabled: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <div>
            <span className="block font-medium text-gray-900 dark:text-white">
              Webhook notifications
            </span>
            <span className="block text-sm text-gray-600 dark:text-gray-400 mt-1">
              Post to one or more URLs (Teams, generic JSON) when an event happens for this zone.
            </span>
          </div>
        </label>

        <div className="space-y-3">
          {form.webhook_targets.map((target, index) => (
            <div
              key={target.target_id}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2 items-center border border-gray-100 dark:border-gray-700 rounded-lg p-3"
            >
              <input
                type="url"
                value={target.url}
                onChange={(e) => updateTarget(index, { url: e.target.value })}
                placeholder="https://..."
                disabled={!form.webhook_enabled}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         disabled:opacity-50"
              />
              <select
                value={target.format}
                onChange={(e) => updateTarget(index, { format: e.target.value as 'generic' | 'teams' })}
                disabled={!form.webhook_enabled}
                className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              >
                <option value="generic">Generic JSON</option>
                <option value="teams">Teams</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={target.enabled}
                  onChange={(e) => updateTarget(index, { enabled: e.target.checked })}
                  disabled={!form.webhook_enabled}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Enabled
              </label>
              <button
                type="button"
                onClick={() => removeTarget(index)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 p-1"
                title="Remove this webhook"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
          <Button variant="ghost" size="sm" leadingIcon={<PlusIcon className="h-4 w-4" />} onClick={addTarget}>
            Add webhook
          </Button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Webhook URLs must start with https://.
        </p>
      </div>

      {/* SMTP email */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.smtp_enabled}
            onChange={(e) => setForm({ ...form, smtp_enabled: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <div>
            <span className="block font-medium text-gray-900 dark:text-white">
              Email notifications
            </span>
            <span className="block text-sm text-gray-600 dark:text-gray-400 mt-1">
              Send email via SMTP for approval requests, decisions, and lifecycle changes.
            </span>
          </div>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMTP host
            </label>
            <input
              type="text"
              value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              disabled={!form.smtp_enabled}
              placeholder="smtp.example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMTP port
            </label>
            <input
              type="number"
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })}
              disabled={!form.smtp_enabled}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMTP username
            </label>
            <input
              type="text"
              value={form.smtp_username}
              onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
              disabled={!form.smtp_enabled}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMTP password {form.smtp_password_is_set && !form.smtp_password && (
                <span className="text-xs text-gray-500">(already set -- leave blank to keep)</span>
              )}
            </label>
            <input
              type="password"
              value={form.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              disabled={!form.smtp_enabled}
              placeholder={form.smtp_password_is_set ? '********' : ''}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From address
            </label>
            <input
              type="email"
              value={form.smtp_from_address}
              onChange={(e) => setForm({ ...form, smtp_from_address: e.target.value })}
              disabled={!form.smtp_enabled}
              placeholder="registry@example.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mt-6">
            <input
              type="checkbox"
              checked={form.smtp_use_tls}
              onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })}
              disabled={!form.smtp_enabled}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Use TLS
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Recipients (comma-separated)
          </label>
          <input
            type="text"
            value={form.email_recipients}
            onChange={(e) => setForm({ ...form, email_recipients: e.target.value })}
            disabled={!form.smtp_enabled}
            placeholder="alice@example.com, bob@example.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" onClick={handleSave} disabled={!hasChanges || saving} className="px-6">
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
};

export default ZoneNotificationSettings;
